import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import {
  type ListOrgFunctionsItem,
  type OrgFunction,
  type AddOrgFunctionRequest,
  type UpdateOrgFunctionRequest,
  type AddOrgFunctionResponse,
  type UpdateOrgFunctionResponse,
  type DeleteOrgFunctionResponse
} from '@bliks-io/contracts';
import { useDepartmentTeams } from '@/api/queries/useOrgFunctions';
import {
  useCreateOrgFunction,
  useUpdateOrgFunction,
  useDeleteOrgFunction
} from '@/api/mutations/useOrgFunctions';
import { useCrudDialogState } from '@/hooks/useCrudDialogState';
import { useCrudHandlers } from '@/hooks/useCrudHandlers';
import { useManagedDepartmentSelection } from '@/hooks/useManagedDepartmentSelection';
import {
  DeleteConfirmDialog,
  Button,
  Page,
  PageHeader,
  PageContent,
  DataTable,
  createActionsColumn,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { OrgFunctionFormDialog } from '@/pages/organization/org-functions/components/OrgFunctionFormDialog';
import { orgFunctionDetailRoute } from '@/lib/orgFunctionRoutes';
import { useAuthStore } from '@/stores/auth-store';
import { ContentHeader } from '@/components/content-header';
import { DepartmentTabsNav } from '@/components/department/DepartmentTabsNav';
import { WorkEnvironmentTabsNav } from '@/components/work-environment/WorkEnvironmentTabsNav';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { ManagedDepartmentSelector } from '@/components/department/ManagedDepartmentSelector';
import {
  buildOrgFunctionNameColumn,
  buildOrgFunctionLeadColumn,
  buildOrgFunctionMemberCountColumn
} from '@/pages/organization/org-functions/utils/orgFunctionColumns';

export function DepartmentTeamsListPage() {
  const { t } = useTranslation();
  useDocumentTitle('pageTitles.departmentTeams');
  const slug = useAuthStore().slug();
  const navigate = useNavigate();
  const { dept: selectedDeptFromUrl } = useSearch({ from: '/$slug/department/teams/' });
  const {
    managedDepartments,
    defaultDepartmentId,
    selectedDepartmentId,
    selectedDepartment,
    handleDepartmentChange
  } = useManagedDepartmentSelection('/$slug/department/teams', selectedDeptFromUrl);

  const { data: departmentTeams, isLoading } = useDepartmentTeams(selectedDepartmentId);

  // Mutations
  const createMutation = useCreateOrgFunction();
  const updateMutation = useUpdateOrgFunction();
  const deleteMutation = useDeleteOrgFunction();

  // CRUD dialog state management
  const {
    formDialogOpen,
    editingEntity: editingOrgFunction,
    deleteDialogOpen,
    deletingEntityId: deletingOrgFunctionId,
    openCreate: handleCreate,
    openEdit: handleEdit,
    openDelete: handleDelete,
    closeFormDialog,
    closeDeleteDialog
  } = useCrudDialogState<OrgFunction>();

  // CRUD handlers with error handling
  const { handleFormSubmit, handleDeleteConfirm, isDeleting } = useCrudHandlers<
    OrgFunction,
    AddOrgFunctionRequest,
    UpdateOrgFunctionRequest,
    AddOrgFunctionResponse,
    UpdateOrgFunctionResponse,
    DeleteOrgFunctionResponse
  >({
    createMutation,
    updateMutation,
    deleteMutation,
    editingEntity: editingOrgFunction,
    deletingEntityId: deletingOrgFunctionId,
    t,
    onFormSuccess: closeFormDialog,
    onDeleteSuccess: closeDeleteDialog
  });

  // View handler
  const handleView = useCallback(
    (orgFunction: OrgFunction) => {
      navigate({
        to: orgFunctionDetailRoute('team'),
        params: { slug, orgFunctionId: orgFunction._id }
      });
    },
    [navigate, slug]
  );

  // Define columns with stable reference using useMemo
  const columns = useMemo<DataTableColumnDef<ListOrgFunctionsItem>[]>(
    () => [
      buildOrgFunctionNameColumn(t, slug),
      buildOrgFunctionLeadColumn(t),
      buildOrgFunctionMemberCountColumn(t),
      createActionsColumn<ListOrgFunctionsItem>({
        onEdit: (orgFunction) => handleEdit(orgFunction),
        onDelete: (orgFunction) => handleDelete(orgFunction._id),
        onView: handleView
      })
    ],
    [t, slug, handleEdit, handleDelete, handleView]
  );

  // Show message if user is not a department manager
  if (!defaultDepartmentId) {
    return (
      <Page>
        <PageHeader />
        <PageContent>
          <ContentHeader
            icon={ENTITY_ICONS.team}
            title={t('department.teams.title')}
            description={t('department.teams.noAccess')}
          />
        </PageContent>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader>
        <WorkEnvironmentTabsNav />
        <DepartmentTabsNav />
      </PageHeader>
      <PageContent>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <ContentHeader
              icon={ENTITY_ICONS.team}
              title={t('department.teams.title')}
              description={
                selectedDepartment
                  ? t('department.teams.description', { department: selectedDepartment.name })
                  : t('department.teams.descriptionDefault')
              }
            />
            <ManagedDepartmentSelector
              departments={managedDepartments}
              selectedDepartmentId={selectedDepartmentId}
              onDepartmentChange={handleDepartmentChange}
              className="w-[250px]"
            />
          </div>
          <Button variant="primary" size="medium" onClick={handleCreate}>
            <Plus className="h-4 w-4" />
            {t('department.teams.createTeam')}
          </Button>
        </div>

        <div className="mt-8">
          <DataTable
            columns={columns}
            data={departmentTeams}
            isLoading={isLoading}
            emptyMessage={t('department.teams.empty')}
            enableGlobalFilter
            globalSearchPlaceholder={t('department.teams.search')}
            enableColumnVisibility
            enableColumnFilters
            enableUrlState
            tableId="departmentTeams"
          />
        </div>

        {/* Form Dialog — creates are team-kind, seeded into the selected department */}
        <OrgFunctionFormDialog
          open={formDialogOpen}
          onOpenChange={(open) => !open && closeFormDialog()}
          onSubmit={handleFormSubmit}
          kind="team"
          orgFunction={editingOrgFunction}
          defaultValuesOverride={
            editingOrgFunction ? undefined : { department: selectedDepartmentId }
          }
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={(open) => !open && closeDeleteDialog()}
          onConfirm={handleDeleteConfirm}
          isDeleting={isDeleting}
          entityPhrase={t('organization.orgFunctions.deletePhrase.team')}
        />
      </PageContent>
    </Page>
  );
}
